import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CloseRounded,
  ContentCopyOutlined,
  DeleteOutline,
  DragIndicator,
  MailOutline,
  PersonOutline,
  SendOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, Chip, CircularProgress, Collapse, Divider, FormControl, IconButton, InputAdornment, List, ListItem, MenuItem, Select, Skeleton, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { EBenefitTypes } from "../../permissions/benefits/enums/benefits.enums";
import { selectEntitlement } from "../../permissions/benefits/redux/benefits.selectors";
import { selectUser } from "../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../redux/hooks/redux";
import { menuPropsModal, updateMyNotificatioById } from "../../utils";
import { useCancelInvitation } from "../invitations/hooks/useCancelInvitation";
import { useGetInvitations, useGetReceivedInvitations } from "../invitations/hooks/useInvitations";
import { colors, alphaVar } from "../ui/colors";
import { useDialog } from "../ui/dialogs/useDialog";
import { useChangeAdminOrder } from "./hooks/useChangeAdminOrder";
import { useGetBusinessOwner } from "./hooks/useGetBusinessOwner";
import { useRemoveAdmin } from "./hooks/useRemoveAdmin";
import { useSendUserInvitation } from "./hooks/useSendUserInvitation";
import { EUserOrgRole, normalizeRole } from "./utils/admin-role.util";
import { AdminInvitationValidate } from "./AdminAcceptInvitation";
import { useDispatch, useSelector } from "react-redux";
import { updateNotifications } from "src/redux/features/notificationSlice";

// ─── Constants ───────────────────────────────────────────────────────────────

export enum EInvitationTargetOption {
  VR_INVITATION = "vr_invitation",
  MANAGER_INVITATION = "manager_invitation",
}

const INVITE_LABEL: Record<EInvitationTargetOption, string> = {
  [EInvitationTargetOption.VR_INVITATION]: "Virtual receptionist",
  [EInvitationTargetOption.MANAGER_INVITATION]: "Account manager",
};

const INVITE_TYPE_LABEL: Record<string, string> = {
  vr_invitation: "Virtual receptionist",
  manager_invitation: "Account manager",
  workforce_invitation: "Workforce",
};

const ROLE_LABEL: Record<EUserOrgRole, string> = {
  [EUserOrgRole.VR]: "Virtual receptionist",
  [EUserOrgRole.Manager]: "Account manager",
  [EUserOrgRole.Admin]: "Admin",
  [EUserOrgRole.Business]: "Owner",
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const sectionTitleSx = {
  fontWeight: 1000,
  color: colors.gray.base,
  fontSize: { xs: 18, md: 20 },
  mb: 1,
};

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
  background: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.9)",
};

const listItemSx = { flexDirection: "row", alignItems: "center", px: 2, py: 2 };
const listItemContentSx = { flexDirection: "column", alignItems: "flex-start", flex: 1, ml: 1 };

const chipBaseSx = { height: 24, borderRadius: "999px", fontWeight: 900, fontSize: 11 };

const orangeChipSx = {
  ...chipBaseSx,
  bgcolor: "rgba(251,142,0,0.10)",
  color: colors.orange.base,
  border: `1px solid rgba(251,142,0,0.25)`,
  textTransform: "capitalize" as const,
};

const deleteIconButtonSx = {
  backgroundColor: colors.gray[100],
  "&:hover": { backgroundColor: colors.gray[200] },
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface IAdminRow {
  order: number;
  uid: string;
  name: string;
  email: string;
  role: EUserOrgRole;
}

// ─── Sortable Item Component ──────────────────────────────────────────────────

interface SortableAdminRowProps {
  admin: IAdminRow;
  index: number;
  isLast: boolean;
  isXs: boolean;
  onRemove: (admin: IAdminRow) => void;
  canDrag: boolean;
}

const getPriorityColors = (priority: number) => {
  if (priority === 1) return { bg: alphaVar(colors.orange.base, 0.1), text: colors.orange[700], border: alphaVar(colors.orange.base, 0.2) };
  if (priority === 2) return { bg: alphaVar(colors.blue.base, 0.1), text: colors.blue[700], border: alphaVar(colors.blue.base, 0.2) };
  return { bg: alphaVar(colors.gray[400], 0.08), text: colors.gray[500], border: alphaVar(colors.gray[400], 0.15) };
};

const priorityBadgeSx = (priority: number) => {
  const palette = getPriorityColors(priority);
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: 0.75,
    py: 0.25,
    borderRadius: "6px",
    fontSize: "9px",
    fontWeight: 1000,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    backgroundColor: palette.bg,
    color: palette.text,
    border: `1px solid ${palette.border}`,
    lineHeight: 1,
    height: "18px",
  };
};

const SortableAdminRow = ({ admin, index, isLast, isXs, onRemove, canDrag }: SortableAdminRowProps) => {
  const priority = index + 1;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: admin.uid, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    position: "relative" as const,
    backgroundColor: isDragging ? "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)" : "transparent",
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <ListItem
        secondaryAction={
          !isXs ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Chip label={ROLE_LABEL[admin.role]} sx={orangeChipSx} />
              {admin.role !== EUserOrgRole.Business && (
                <IconButton onClick={() => onRemove(admin)} size="small" sx={deleteIconButtonSx}>
                  <DeleteOutline sx={{ fontSize: 18, color: colors.orange[500] }} />
                </IconButton>
              )}
            </Box>
          ) : null
        }
        sx={listItemSx}
      >
        {canDrag ? (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: isDragging ? "grabbing" : "grab",
              display: "flex",
              alignItems: "center",
              color: colors.gray[300],
              "&:hover": { color: colors.gray[500] },
              mr: 1,
            }}
          >
            <DragIndicator sx={{ fontSize: 20 }} />
          </Box>
        ) : (
          <Box sx={{ width: 20, mr: 1 }} />
        )}

        {admin.role === EUserOrgRole.Business ? (
          <ShieldOutlined sx={{ color: colors.gray[300], fontSize: 20, mr: 1 }} />
        ) : (
          <PersonOutline sx={{ color: colors.gray[300], fontSize: 20, mr: 1 }} />
        )}

        <Box sx={listItemContentSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 1000, fontSize: 13, color: colors.gray[600] }}>{admin.name}</Typography>
            <Tooltip title={`Priority Level ${priority}`}>
              <Box sx={priorityBadgeSx(priority)}>#{priority}</Box>
            </Tooltip>
            {index === 0 && <Box sx={priorityBadgeSx(priority)}>Highest Relevance</Box>}
            {isLast && index > 0 && <Box sx={priorityBadgeSx(priority)}>Lowest Relevance</Box>}
          </Box>
          <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: 11 }}>{admin.email}</Typography>

          {isXs && (
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 1.5, alignItems: "center" }}>
              <Chip label={ROLE_LABEL[admin.role]} sx={orangeChipSx} />
              {admin.role !== EUserOrgRole.Business && (
                <IconButton onClick={() => onRemove(admin)} size="small" sx={deleteIconButtonSx}>
                  <DeleteOutline sx={{ fontSize: 18, color: colors.orange[500] }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </ListItem>
    </Box>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const ManageAdmins = () => {
  const { showSnackbar } = useGlobalSnackbar();
  const { showDialog, DialogComponent } = useDialog();
  const isXs = useMediaQuery("(max-width:600px)");
  const [selectedInvitationUid, setSelectedInvitationUid] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [inviteTarget, setInviteTarget] = useState<EInvitationTargetOption>(EInvitationTargetOption.VR_INVITATION);
  const [acceptedInvitationUid, setAcceptedInvitationUid] = useState<string | null>(null);
  const [confirmRejectInvitationUid, setConfirmRejectInvitationUid] = useState<string | null>(null);
  const currentUser = useAppSelector(selectUser);
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const notifications = useSelector((state: any) => state.notificationsState.notifications);
  const businessOwnerQuery = useGetBusinessOwner();
  const isLoading = businessOwnerQuery.isLoading;
  const isError = businessOwnerQuery.isError;
  const handleOpenInvitation = (uid: string) => {
    setSelectedInvitationUid(uid);
  };

  const handleCloseInvitation = () => {
    setSelectedInvitationUid(null);
    setAcceptedInvitationUid(null);
    setConfirmRejectInvitationUid(null);

    receivedInvitationsQuery.refetch();
    invitationsQuery.refetch();
  };
  const markInvitationNotificationAsProcessed = async (invitationUid: string, status: "accepted" | "canceled", notificationId?: number) => {
    const matchingNotifications = notifications.filter((notification: any) => {
      const meta = notification?.meta || {};

      return (
        notification.type === "adminInvitationReceived" &&
        (notification.id === notificationId || meta.id === invitationUid || meta.uid === invitationUid)
      );
    });

    const updatedNotifications = notifications.map((notification: any) => {
      const meta = notification?.meta || {};

      const isSameInvitation =
        notification.type === "adminInvitationReceived" &&
        (notification.id === notificationId || meta.id === invitationUid || meta.uid === invitationUid);

      if (!isSameInvitation) return notification;

      return {
        ...notification,
        isRead: true,
        readAt: new Date().toISOString(),
        meta: {
          ...meta,
          status,
        },
      };
    });

    dispatch(updateNotifications({ notifications: updatedNotifications }));

    await Promise.allSettled(
      matchingNotifications.map((notification: any) =>
        updateMyNotificatioById(token, notification.id, {
          isRead: true,
          readAt: new Date().toISOString(),
          meta: {
            ...(notification.meta || {}),
            status,
          },
        }),
      ),
    );
  };

  const ownerEntry = useMemo(() => {
    const people: any[] = businessOwnerQuery.data?.data ?? [];
    if (people.length === 0) return null;

    // 1. Explicit isOwner flag (new accounts, once backend sets it)
    // 2. Legacy role type "business"
    // 3. Earliest createdAt (the company creator)
    return (
      people.find((p: any) => p.isOwner === true) ??
      people.find((p: any) => p.userRole?.role?.type === "business") ??
      [...people].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0] ??
      null
    );
  }, [businessOwnerQuery.data]);

  const isBusinessOwner = !!(currentUser.uid && ownerEntry?.userUid && currentUser.uid === ownerEntry.userUid);
  const ownerEmail = ownerEntry?.user?.email || "-";

  const numberOfSeats = useAppSelector(selectEntitlement(EBenefitTypes.SEATS));

  const sendInviteMutation = useSendUserInvitation();
  const cancelInvitationMutation = useCancelInvitation();
  const removeAdminMutation = useRemoveAdmin();

  const invitationsQuery = useGetInvitations({ page: 1, pageSize: 50 });
  const receivedInvitationsQuery = useGetReceivedInvitations({ page: 1, pageSize: 50 });
  const [orderedAdmins, setOrderedAdmins] = useState<IAdminRow[]>([]);
  const [isPendingExpanded, setIsPendingExpanded] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);

  const changeAdminOrderMutation = useChangeAdminOrder();

  useEffect(() => {
    const { limit, used } = numberOfSeats ?? {};
    if (limit === undefined || used === undefined) return;
    const remaining = Math.max(0, limit - used);
    showSnackbar(
      `The use of this feature may consume seats from your plan. You have ${remaining} seat${remaining !== 1 ? "s" : ""} remaining.`,
      "info",
    );
  }, [numberOfSeats]);

  const allMembers = useMemo<IAdminRow[]>(() => {
    const people: any[] = businessOwnerQuery.data?.data ?? [];
    const ownerUid = ownerEntry?.userUid;

    return people.map((p: any) => ({
      uid: p.userUid,
      name: `${(p.user?.firstName || "").trim()} ${(p.user?.lastName || "").trim()}`.trim() || p.user?.email || "-",
      email: p.user?.email || "-",
      role: p.userUid === ownerUid ? EUserOrgRole.Business : normalizeRole(p.userRole?.role?.type),
      order: p.order ?? 0,
    }));
  }, [businessOwnerQuery.data, ownerEntry]);

  const currentAdminsFiltered = useMemo<IAdminRow[]>(() => allMembers.filter((u) => u.role !== EUserOrgRole.Business), [allMembers]);

  // Sync orderedAdmins with member data.
  // Business owners see everyone in the reorderable list; others see only admins.
  useEffect(() => {
    const baseList = isBusinessOwner ? allMembers : currentAdminsFiltered;

    if (baseList.length === 0) {
      setOrderedAdmins([]);
      return;
    }

    setOrderedAdmins((prev) => {
      if (prev.length === 0) {
        return [...baseList].sort((a, b) => a.order - b.order);
      }

      const prevIds = new Set(prev.map((a) => a.uid));
      const newOnes = baseList.filter((a) => !prevIds.has(a.uid));
      const remaining = prev.filter((a) => baseList.some((ca) => ca.uid === a.uid));
      const updatedRemaining = remaining.map((a) => baseList.find((ca) => ca.uid === a.uid) || a);

      return [...updatedRemaining, ...newOnes];
    });
  }, [allMembers, isBusinessOwner, currentAdminsFiltered]);
  {
    /* ── Invitations to accept ── */
  }

  const handleRejectInvite = (inv: { uid: string; invitationUid?: string }) => {
    const invitationUid = inv.invitationUid || inv.uid;

    setSelectedInvitationUid(invitationUid);
    setConfirmRejectInvitationUid(invitationUid);
  };

  const pendingInvites = useMemo(
    () =>
      (invitationsQuery.data?.data?.items || [])
        .filter((x: any) => {
          const status = x.status?.toLowerCase();
          const inviteeEmail = x.inviteeIdentifier?.toLowerCase();
          const currentEmail = currentUser?.email?.toLowerCase();

          return status === "sent" && inviteeEmail !== currentEmail;
        })
        .map((x: any) => {
          const created = DateTime.fromISO(x.createdAt || "").toLocal();

          return {
            uid: x.uid,

            raw: x,
            email: x.inviteeIdentifier,
            type: x.type?.toLowerCase() || "",
            status: x.status?.toLowerCase()?.trim(),
            createdAt: created.isValid ? created.toFormat("yyyy-LL-dd HH:mm") : "-",
            link: x.link,
          };
        })
        .sort((a: any, b: any) => a.createdAt.localeCompare(b.createdAt)),
    [invitationsQuery.data, currentUser?.email],
  );

  const invitationsToAccept = useMemo(
    () =>
      (receivedInvitationsQuery.data?.data?.items || []).map((x: any) => {
        const created = DateTime.fromISO(x.createdAt || "").toLocal();

        return {
          uid: x.uid,
          invitationUid: x.uid,
          companyId: x.companyId,
          notificationId: x.notificationId,
          raw: x,
          email: x.inviteeIdentifier,
          type: x.type?.toLowerCase() || "",
          status: x.status?.toLowerCase()?.trim(),
          createdAt: created.isValid ? created.toFormat("yyyy-LL-dd HH:mm") : "-",
          link: x.link,
        };
      }),
    [receivedInvitationsQuery.data],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedAdmins((items) => {
        const oldIndex = items.findIndex((i) => i.uid === active.id);
        const newIndex = items.findIndex((i) => i.uid === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        changeAdminOrderMutation.mutate(newOrder.map((u) => u.uid));
        return newOrder;
      });
    }
  };

  const handleSendInvite = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return showSnackbar("Enter an email address.", "warning");
    try {
      await sendInviteMutation.mutateAsync({ email: normalized, target: inviteTarget });
      setEmail("");
      showSnackbar("Invitation sent.", "success");
      const refetched = await invitationsQuery.refetch();
    } catch (e: any) {
      showSnackbar(e?.response?.data?.message || e?.message || "Could not send invitation.", "error");
    }
  };
  const handleCancelInvite = (inv: { uid: string; email: string }) => {
    showDialog({
      type: "confirm",
      title: "Cancel Invitation",
      message: `Cancel invitation for "${inv.email}"?`,
      confirmLabel: "Cancel invite",
      cancelLabel: "Close",
      onConfirm: async () => {
        try {
          await cancelInvitationMutation.mutateAsync(inv.uid);
          showSnackbar("Invitation canceled.", "success");
          await invitationsQuery.refetch();
        } catch (e: any) {
          showSnackbar(e?.response?.data?.message || e?.message || "Could not cancel invitation.", "error");
        }
      },
      onCancel: () => {},
    });
  };

  const InvitationToAcceptActions = ({ inv }: { inv: any }) => {
    const isCanceled = inv.status === "canceled";
    const canAccept = inv.status === "sent";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isXs ? "space-between" : "flex-end",
          width: isXs ? "100%" : "auto",
          gap: 1,
        }}
      >
        <Chip label={INVITE_TYPE_LABEL[inv.type] || inv.type || "-"} sx={orangeChipSx} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={!canAccept}
            onClick={() => handleOpenInvitation(inv.invitationUid || inv.uid)}
            sx={{
              height: 30,
              px: 1.5,
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 900,
              textTransform: "none",
              boxShadow: "none",
              bgcolor: canAccept ? colors.orange.base : colors.gray[200],
              color: canAccept ? "#fff" : colors.gray[500],
              "&:hover": {
                bgcolor: canAccept ? colors.orange[600] : colors.gray[200],
                boxShadow: "none",
              },
            }}
          >
            {isCanceled ? "Canceled" : "Accept"}
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={isCanceled || cancelInvitationMutation.isPending}
            onClick={() => handleRejectInvite(inv)}
            sx={{
              height: 30,
              px: 1.5,
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 900,
              textTransform: "none",
              color: colors.gray[500],
              borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.16)",
              "&:hover": {
                borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.28)",
                bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.03)",
              },
            }}
          >
            Decline
          </Button>
        </Box>
      </Box>
    );
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      showSnackbar("Invite link copied.", "success");
    } catch {
      showSnackbar("Could not copy link.", "error");
    }
  };

  const handleRemoveAdmin = (admin: IAdminRow) => {
    showDialog({
      type: "confirm",
      title: "Remove Member",
      message: `Are you sure you want to remove "${admin.email}" from your staff? This action cannot be undone.`,
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await removeAdminMutation.mutateAsync(admin.uid);
          showSnackbar("Admin removed.", "success");
        } catch (e: any) {
          showSnackbar(e?.response?.data?.message || e?.message || "Could not remove member.", "error");
        }
      },
      onCancel: () => {},
    });
  };

  const SendButton = (
    <Button
      onClick={handleSendInvite}
      disabled={sendInviteMutation.isPending}
      variant="contained"
      size="small"
      sx={{
        minWidth: 44,
        height: 38,
        px: 1.25,
        borderRadius: "10px",
        boxShadow: "none",
        backgroundColor: colors.orange.base,
        "&:hover": { backgroundColor: colors.orange[600] },
      }}
    >
      {sendInviteMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <SendOutlined sx={{ fontSize: 18 }} />}
    </Button>
  );

  const InviteActions = ({ inv }: { inv: any }) => {
    const canAcceptInvitation = currentUser?.email?.toLowerCase() === inv.email?.toLowerCase();

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isXs ? "space-between" : "flex-end",
          width: isXs ? "100%" : "auto",
          gap: isXs ? 0 : 1.5,
        }}
      >
        <Chip label={INVITE_TYPE_LABEL[inv.type] || inv.type || "-"} sx={orangeChipSx} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {canAcceptInvitation && (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleOpenInvitation(inv.uid)}
              sx={{
                height: 30,
                px: 1.5,
                borderRadius: "999px",
                fontSize: 11,
                fontWeight: 900,
                textTransform: "none",
                boxShadow: "none",
                bgcolor: colors.orange.base,
                "&:hover": {
                  bgcolor: colors.orange[600],
                  boxShadow: "none",
                },
              }}
            >
              Accept
            </Button>
          )}

          {inv.link && (
            <IconButton onClick={() => handleCopyLink(inv.link)} size="small">
              <ContentCopyOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          <IconButton onClick={() => handleCancelInvite(inv)} size="small" disabled={cancelInvitationMutation.isPending} sx={deleteIconButtonSx}>
            {cancelInvitationMutation.isPending ? <CircularProgress size={18} /> : <DeleteOutline sx={{ fontSize: 18, color: colors.orange[500] }} />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const OwnerChip = ({ small }: { small?: boolean }) => (
    <Chip
      label={isLoading ? "Loading" : "Owner"}
      sx={{
        ...chipBaseSx,
        ...(small && { height: 22 }),
        bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
        color: colors.gray.base,
        border: `1px solid rgba(12,12,12,0.08)`,
      }}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {DialogComponent}

      {selectedInvitationUid ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            bgcolor: "rgba(15, 23, 42, 0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 560,
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleCloseInvitation}
              sx={{
                position: "absolute",
                top: 14,
                right: 14,
                zIndex: 2,
                width: 36,
                height: 36,
                borderRadius: "12px",
                bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.92)",
                border: "1px solid rgba(229,231,235,0.95)",
                boxShadow: "0px 8px 22px rgba(15, 23, 42, 0.14)",
                "&:hover": {
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                },
              }}
            >
              <CloseRounded sx={{ fontSize: 19 }} />
            </IconButton>

            {confirmRejectInvitationUid === selectedInvitationUid ? (
              <Box
                sx={{
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: "24px",
                  p: 4,
                  textAlign: "center",
                  boxShadow: "0px 24px 70px rgba(15, 23, 42, 0.22)",
                }}
              >
                <Typography sx={{ fontSize: 22, fontWeight: 1000, color: colors.gray.base, mb: 1 }}>Decline Invitation</Typography>

                <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.gray[500], mb: 3 }}>
                  Are you sure you want to decline this company invitation?
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                  <Button
                    onClick={handleCloseInvitation}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      fontWeight: 900,
                      color: colors.gray[500],
                      bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                      px: 2.5,
                      "&:hover": {
                        bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.07)",
                      },
                    }}
                  >
                    Maybe later
                  </Button>

                  <Button
                    variant="contained"
                    disabled={cancelInvitationMutation.isPending}
                    onClick={async () => {
                      const inv = invitationsToAccept.find((x: any) => x.uid === selectedInvitationUid || x.invitationUid === selectedInvitationUid);

                      if (!inv) return;

                      const invitationUid = inv.invitationUid || inv.uid;

                      try {
                        await cancelInvitationMutation.mutateAsync({
                          invitationUid,
                          companyId: inv.companyId,
                        });

                        await markInvitationNotificationAsProcessed(invitationUid, "canceled", inv.notificationId);

                        showSnackbar("Invitation declined.", "success");
                        handleCloseInvitation();
                      } catch (e: any) {
                        showSnackbar(e?.response?.data?.message || e?.message || "Could not decline invitation.", "error");
                      }
                    }}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: "#B91C1C",
                      px: 2.5,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#991B1B",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Decline
                  </Button>
                </Box>
              </Box>
            ) : (
              <AdminInvitationValidate
                variant="modal"
                invitationUid={selectedInvitationUid}
                onClose={handleCloseInvitation}
                onAccepted={async () => {
                  if (selectedInvitationUid) {
                    await markInvitationNotificationAsProcessed(selectedInvitationUid, "accepted");
                  }

                  showSnackbar("Invitation accepted successfully.", "success");
                  handleCloseInvitation();
                }}
                onRejectClick={() => {
                  setConfirmRejectInvitationUid(selectedInvitationUid);
                }}
              />
            )}
          </Box>
        </Box>
      ) : null}

      <Typography sx={{ fontWeight: 1000, color: colors.gray.base, fontSize: { xs: 22, md: 28 }, mb: 0.5 }}>Manage Members</Typography>
      <Typography sx={{ color: colors.gray[400], fontWeight: 600, mb: 2 }}>
        Invite new company members, track invitation status, and remove existing members.
      </Typography>

      {/* ── Invite bar ── */}
      <Box
        sx={{
          borderRadius: "12px",
          border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
          background: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.75)",
          px: { xs: 1.5, md: 1.25 },
          py: { xs: 1.5, md: 0.75 },
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: 1.5,
          width: { xs: "100%", md: "70%", lg: "60%" },
        }}
      >
        <Typography
          sx={{ fontWeight: 900, color: colors.gray.base, fontSize: 13, whiteSpace: "nowrap", display: { xs: "none", md: "block" }, mr: 0.5 }}
        >
          Invite
        </Typography>

        <Box sx={{ display: { xs: "flex", md: "contents" }, width: { xs: "100%", md: "auto" }, gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: { xs: "70%", md: 170 }, flex: { xs: 1, md: "none" } }}>
            <Select
              value={inviteTarget}
              onChange={(e) => setInviteTarget(e.target.value as EInvitationTargetOption)}
              MenuProps={menuPropsModal}
              sx={{
                height: 38,
                borderRadius: "10px",
                background: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
                fontSize: 14,
                fontWeight: 800,
                color: colors.gray.base,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.16)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.orange.base, borderWidth: "1px" },
              }}
            >
              {Object.values(EInvitationTargetOption).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {INVITE_LABEL[opt]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: { xs: "block", md: "none" } }}>{SendButton}</Box>
        </Box>

        <TextField
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          size="small"
          placeholder="name@company.com"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutline sx={{ color: colors.gray[400], fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { height: 38, borderRadius: "10px", background: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)" },
            "& .MuiOutlinedInput-input": { py: 0, fontSize: 14, fontWeight: 700 },
            "& fieldset": { borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)" },
            "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.16)" },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "rgba(251,142,0,0.55)" },
          }}
        />

        <Box sx={{ display: { xs: "none", md: "block" } }}>{SendButton}</Box>
      </Box>

      {/* ── Current members ── */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 0.5, gap: 2 }}>
          <Typography sx={sectionTitleSx}>
            Current members <span style={{ fontSize: 12, opacity: 1 }}>({orderedAdmins.length})</span>
          </Typography>
          <IconButton onClick={() => setIsDescriptionOpen((v) => !v)} aria-label={isDescriptionOpen ? "Collapse description" : "Expand description"}>
            <InfoIcon sx={{ color: "#9ad3ffff" }} />
          </IconButton>
        </Box>

        <Collapse in={isDescriptionOpen} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, bgcolor: "var(--fx-colors-info-100, #DBEAFE)", borderRadius: 2, border: "1px solid var(--fx-surface-border, #E2E8F0)", mb: 2 }}>
            <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.6, fontSize: "0.875rem" }}>
              Manage your company members here. Invite new members, track invitation status, remove existing members, and change their relevance in
              the organization (the more relevance the higher priority in the phone call queue).
            </Typography>
          </Box>
        </Collapse>

        <Box sx={cardSx}>
          <List dense={!isXs} sx={{ py: 0 }}>
            {/* Owner row is shown separately only when the current user is not the business owner */}
            {!isBusinessOwner && (
              <>
                <ListItem secondaryAction={!isXs ? <OwnerChip /> : null} sx={{ ...listItemSx, pl: 2 }}>
                  <Box sx={{ width: 20, mr: 1 }} />
                  <PersonOutline sx={{ color: colors.gray[300], fontSize: 20, mr: 1 }} />
                  <Box sx={listItemContentSx}>
                    <Typography sx={{ fontWeight: 1000, fontSize: 13, color: colors.gray[600], mb: 0.5 }}>Business owner</Typography>
                    <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: 11 }}>
                      {isError ? "Could not load owner" : ownerEmail}
                    </Typography>
                    {isXs && (
                      <Box sx={{ mt: 1.5 }}>
                        <OwnerChip small />
                      </Box>
                    )}
                  </Box>
                </ListItem>
                <Divider sx={{ mx: 2 }} />
              </>
            )}

            {isLoading ? (
              Array.from({ length: 2 }).map((_, idx) => (
                <Box key={idx}>
                  <ListItem sx={{ px: 2, py: 2, flexDirection: "column", alignItems: "flex-start" }}>
                    <Skeleton width="40%" height={20} />
                    <Skeleton width="60%" height={18} />
                  </ListItem>
                  {idx < 1 && <Divider sx={{ mx: 2 }} />}
                </Box>
              ))
            ) : isError ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontWeight: 900, color: colors.gray.base, fontSize: 14 }}>Could not load members.</Typography>
              </Box>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedAdmins.map((a) => a.uid)} strategy={verticalListSortingStrategy}>
                  {orderedAdmins.map((a, idx) => (
                    <Box key={a.uid}>
                      <SortableAdminRow
                        admin={a}
                        index={idx}
                        isLast={idx === orderedAdmins.length - 1}
                        isXs={isXs}
                        onRemove={handleRemoveAdmin}
                        canDrag={isBusinessOwner}
                      />
                    </Box>
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </List>
        </Box>
      </Box>
      {/* ── Invitations to accept ── */}
      {(receivedInvitationsQuery.isLoading || invitationsToAccept.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 0.5, gap: 2 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
              Invitations to accept <span style={{ fontSize: 12, opacity: 1 }}>({invitationsToAccept.length})</span>
            </Typography>
          </Box>

          <Box sx={cardSx}>
            <List dense={!isXs} sx={{ py: 0 }}>
              {invitationsToAccept.map((inv: any, idx: number) => (
                <Box key={inv.uid}>
                  <ListItem secondaryAction={!isXs ? <InvitationToAcceptActions inv={inv} /> : null} sx={{ ...listItemSx, pl: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 1000,
                          fontSize: 13,
                          color: colors.gray[600],
                          mb: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {INVITE_TYPE_LABEL[inv.type] || inv.type || "Invitation"}
                      </Typography>

                      <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: 11 }}>Sent to: {inv.email}</Typography>

                      <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: 11 }}>Sent: {inv.createdAt}</Typography>
                    </Box>

                    {isXs && (
                      <Box sx={{ width: "100%", mt: 1.5 }}>
                        <InvitationToAcceptActions inv={inv} />
                      </Box>
                    )}
                  </ListItem>

                  {idx < invitationsToAccept.length - 1 && <Divider sx={{ mx: 2 }} />}
                </Box>
              ))}
            </List>
          </Box>
        </Box>
      )}
      {/* ── Pending invitations ── */}

      {(invitationsQuery.isLoading || pendingInvites.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 0.5, gap: 2 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0, fontWeight: 400, opacity: 0.75 }}>
              Pending invitations <span style={{ fontSize: 12, opacity: 1 }}>({pendingInvites.length})</span>
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsPendingExpanded(!isPendingExpanded)}
              sx={{ transition: "transform 0.3s", transform: isPendingExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
            >
              <ExpandMore />
            </IconButton>
          </Box>

          <Collapse in={isPendingExpanded}>
            <Box sx={cardSx}>
              <List dense={!isXs} sx={{ py: 0 }}>
                {pendingInvites.map((inv: any, idx: number) => (
                  <Box key={inv.uid}>
                    <ListItem secondaryAction={!isXs ? <InviteActions inv={inv} /> : null} sx={{ ...listItemSx, pl: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 1000,
                            fontSize: 13,
                            color: colors.gray[600],
                            mb: 0.5,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {inv.email}
                        </Typography>
                        <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: 11 }}>Sent: {inv.createdAt}</Typography>
                      </Box>
                      {isXs && (
                        <Box sx={{ width: "100%", mt: 1.5 }}>
                          <InviteActions inv={inv} />
                        </Box>
                      )}
                    </ListItem>
                    {idx < pendingInvites.length - 1 && <Divider sx={{ mx: 2 }} />}
                  </Box>
                ))}
              </List>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
